import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import type { Step1Data } from '../../types/registration'

interface Step1AccountProps {
  data: Step1Data
  onChange: (data: Step1Data) => void
  errors: Partial<Record<keyof Step1Data, string>>
  onNext: () => void
  isCheckingEmail?: boolean
}

export default function Step1Account({ data, onChange, errors, onNext, isCheckingEmail = false }: Step1AccountProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const openCenteredPopup = (url: string) => {
    // Keep popup sizing stable across browsers and test environments.
    const width = 900
    const height = 900
    const screenX = window.screenX ?? (window as Window & { screenLeft?: number }).screenLeft ?? 0
    const screenY = window.screenY ?? (window as Window & { screenTop?: number }).screenTop ?? 0
    const outerWidth = window.outerWidth ?? document.documentElement.clientWidth
    const outerHeight = window.outerHeight ?? document.documentElement.clientHeight

    const left = screenX + Math.max(0, (outerWidth - width) / 2)
    const top = screenY + Math.max(0, (outerHeight - height) / 2)

    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`

    const popup = window.open(url, 'legalPopup', features)
    if (popup) {
      // Best-effort: avoid the popup gaining access to window.opener.
      try {
        popup.opener = null
      } catch {
        // Ignore if cross-origin restrictions apply.
      }
      popup.focus()
    } else {
      // Fallback for popup blockers.
      window.location.assign(url)
    }
  }

  const handleChange = (field: keyof Step1Data) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, [field]: e.target.value })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onNext()
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {/* Full Name */}
      <div>
        <label htmlFor="full_name" className="block text-sm font-medium text-rd-muted mb-1">
          Full Name <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <input
          id="full_name"
          type="text"
          autoComplete="name"
          value={data.full_name}
          onChange={handleChange('full_name')}
          aria-required="true"
          aria-describedby={errors.full_name ? 'full_name-error' : undefined}
          aria-invalid={!!errors.full_name}
          className={`reg-input ${errors.full_name ? 'border-red-500/60' : ''}`}
          placeholder="Jane Smith"
        />
        {errors.full_name && (
          <p id="full_name-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.full_name}
          </p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-rd-muted mb-1">
          Email Address <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={data.email}
          onChange={handleChange('email')}
          aria-required="true"
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
          className={`reg-input ${errors.email ? 'border-red-500/60' : ''}`}
          placeholder="jane@example.com"
        />
        {errors.email && (
          <p id="email-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.email}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-rd-muted mb-1">
          Password <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={data.password}
            onChange={handleChange('password')}
            aria-required="true"
            aria-describedby={
              errors.password
                ? 'password-error password-hint'
                : 'password-hint'
            }
            aria-invalid={!!errors.password}
            className={`reg-input pr-12 ${errors.password ? 'border-red-500/60' : ''}`}
            placeholder="Min. 8 characters"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-rd-dim hover:text-rd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-rd-accent/40 focus-visible:ring-inset rounded-r-lg transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <p id="password-hint" className="mt-1 text-xs text-rd-dim">
          At least 8 characters with one uppercase letter, one lowercase letter, and one digit.
        </p>
        {errors.password && (
          <p id="password-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.password}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-rd-muted mb-1">
          Confirm Password <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={data.confirmPassword}
            onChange={handleChange('confirmPassword')}
            aria-required="true"
            aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
            aria-invalid={!!errors.confirmPassword}
            className={`reg-input pr-12 ${errors.confirmPassword ? 'border-red-500/60' : ''}`}
            placeholder="Re-enter your password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
            aria-pressed={showConfirmPassword}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-rd-dim hover:text-rd-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-rd-accent/40 focus-visible:ring-inset rounded-r-lg transition-colors"
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p id="confirmPassword-error" role="alert" className="mt-1 text-sm text-red-400">
            {errors.confirmPassword}
          </p>
        )}
      </div>

      {/* Terms & Privacy Consent */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            id="terms_accepted"
            type="checkbox"
            required
            aria-required="true"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 accent-rd-accent"
          />
          <span className="text-sm text-rd-muted leading-relaxed">
            I agree to the{' '}
            <a
              href="/reputedesk-tos.html"
              data-testid="terms-link"
              className="text-rd-accent underline hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.preventDefault()
                openCenteredPopup('/reputedesk-tos.html')
              }}
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="/reputedesk-privacy.html"
              data-testid="privacy-link"
              className="text-rd-accent underline hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.preventDefault()
                openCenteredPopup('/reputedesk-privacy.html')
              }}
            >
              Privacy Policy
            </a>
            . <span aria-hidden="true" className="text-red-400">*</span>
          </span>
        </label>
      </div>

      {/* Next Button */}
      <button
        type="submit"
        disabled={isCheckingEmail}
        className="reg-btn-primary mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isCheckingEmail ? 'Checking…' : 'Next →'}
      </button>

      {/* Already have account */}
      <p className="text-center text-sm text-rd-muted mt-2">
        Already have an account?{' '}
        <a href="/login" className="text-rd-accent underline hover:opacity-80 transition-opacity">
          Log in
        </a>
      </p>
    </form>
  )
}
